// /* eslint-disable jsx-a11y/anchor-is-valid */
import {useState, useEffect} from 'react'
import * as Yup from 'yup'
import clsx from 'clsx'
import {useIntl} from 'react-intl'
import {Link, useLocation, useNavigate} from 'react-router-dom'
import {useSelector, useDispatch} from 'react-redux'
import {useFormik} from 'formik'
import {login} from '../core/_requests'
import md5 from '../../../helpers/Misc'
import * as Auth from '../../../store/ducks/auth.duck'
import CloseIcon from '@mui/icons-material/Close'
import {Button, Box, Alert, IconButton, AlertTitle, Typography} from '@mui/material'
import * as Member from '../../../store/ducks/member.duck'
import * as Entity from '../../../store/ducks/entity.duck'

const loginSchema = Yup.object().shape({
  username: Yup.string().min(3, 'Minimum 3 symbols').max(50, 'Maximum 50 symbols').required('Username is required'),
  password: Yup.string().min(3, 'Minimum 3 symbols').max(50, 'Maximum 50 symbols').required('Password is required')
})

const initialValues = {
  username: '',
  password: ''
}

export function Login(props: any) {
  const dispatch = useDispatch()
  let navigate = useNavigate()
  const intl = useIntl()
  const accreditationDisplay = useSelector((state: any) => state.member.Flags.AccreditationDisplay)
  // const upcomingMaintenanceDisplay = useSelector((state: any) => state.member.Flags.UpcomingMaintenanceDisplay)
  const upcomingMaintenanceMessage = useSelector((state: any) => state.member.Flags.UpcomingMaintenanceMessage)
  const [upcomingMaintenanceDisplay, setUpcomingMaintenanceDisplay] = useState(false)
  const outageDisplay = useSelector((state: any) => state.member.Flags.OutageDisplay)
  const outageMessage = useSelector((state: any) => state.member.Flags.OutageMessage)
  const bypassStatus = useSelector((state: any) => state.member.Flags.BypassStatus)
  const [requestFlags, setRequestFlags] = useState(false)
  const [showIssue, setShowIssue] = useState(false)
  const [accordionOpen, setAccordionOpen] = useState(false)
  const searchParams = useLocation().search
  const bypassToken = new URLSearchParams(searchParams).get('backdoor_pass')

  useEffect(() => {
    dispatch(Auth.actions.logout())
    dispatch(Member.actions.setMemberInitialState())
    dispatch(Entity.actions.setEntityInitialState())

    if (!requestFlags) {
      // dispatch(Member.actions.fetchFlags({bypassToken: bypassToken ? bypassToken : null}))
      setRequestFlags(true)
    }
  }, [dispatch, requestFlags, setRequestFlags, bypassToken])

  const [loading, setLoading] = useState(false)

  const formik = useFormik({
    initialValues,
    validationSchema: loginSchema,
    onSubmit: async (values, {setStatus, setSubmitting}) => {
      setLoading(true)
      try {
        const enc_password = md5(values.password)
        const {data: auth} = await login(values.username, enc_password)
        dispatch(Auth.actions.login(auth))
        // console.log('auth')
        // console.log(auth)

        if (auth.status === '2FA') {
          // console.log('2FA')
          navigate('/auth/totp')
        } else {
          dispatch(Auth.actions.getVHLogin(auth.member_key, auth.token, auth.email))
        }
      } catch (err: any) {
        // console.log(err}
        //console.log(err.response.data)
        // console.log(err.response.status)
        // const message = '<br> Hello World <br>'
        setSubmitting(false)
        setLoading(false)
        if (err) {
          if (err.response) {
            if (err.response.data.message && err.response.data.message === 'Access Locked') {
              setStatus(
                <a href='https://www.afspa.org/secure-form-member-portal-support/' target='_blank' rel='noreferrer'>
                  Your account is locked. Please click here to contact AFSPA for assistance
                </a>
              )
            } else {
              if (err.response.data.message && err.response.data.message === 'Bad') {
                setStatus(
                  intl.formatMessage({
                    id: 'AUTH.VALIDATION.SERVER_OUTAGE'
                  })
                )
              } else {
                if (err.response.data.message && err.response.data.message === 'Outage') {
                  setStatus(outageMessage)
                } else {
                  if (err.response.data.message && err.response.data.message === 'Account Locked') {
                    setStatus(
                      intl.formatMessage({
                        id: 'AUTH.VALIDATION.LOCKOUT_LOGIN'
                      })
                    )
                  } else {
                    setStatus(
                      intl.formatMessage({
                        id: 'AUTH.VALIDATION.INVALID_LOGIN'
                      })
                    )
                  }
                }
              }
            }
          } else {
            setStatus(
              intl.formatMessage({
                id: 'AUTH.VALIDATION.SERVER_OUTAGE'
              })
            )
          }
        } else {
          setStatus(
            intl.formatMessage({
              id: 'AUTH.VALIDATION.SERVER_OUTAGE'
            })
          )
        }

        // setStatus('The login details are incorrect')
      }
    }
  })

  return (
    <>
      {outageDisplay && !bypassStatus ? (
        <div className='mb-5 bg-light-danger p-8 rounded'>
          <div className='text-danger'>{outageMessage}</div>
        </div>
      ) : (
        <form className='form w-100' onSubmit={formik.handleSubmit} noValidate id='kt_login_signin_form'>
          {/* begin::Heading */}
          <div className='text-center mb-11'>
            <h1 className='text-dark fw-bolder mb-3'>Sign In</h1>
          </div>
          {/* begin::Heading */}

          {showIssue && (
            <Box sx={{width: '100%', py: 1}}>
              <Alert
                severity='warning'
                action={
                  <IconButton aria-label='close' color='inherit' size='small' onClick={() => setShowIssue(false)}>
                    <CloseIcon fontSize='inherit' />
                  </IconButton>
                }
                sx={{
                  width: '100%',
                  alignItems: 'flex-start',
                  mx: 'auto',
                  maxWidth: '100%',
                  fontSize: '1rem'
                }}
              >
                <Box sx={{width: '100%'}} className='experience-issues-text'>
                  <AlertTitle sx={{fontSize: '1.1rem', fontWeight: 'bold'}}>We are currently experiencing technical issues</AlertTitle>

                  {/* Custom clickable toggle instead of AccordionSummary */}
                  <Button
                    onClick={() => setAccordionOpen(!accordionOpen)}
                    sx={{
                      textTransform: 'none',
                      px: 0,
                      mb: 1,
                      fontSize: '1rem',
                      fontWeight: 400,
                      color: 'primary.main',
                      '&:hover': {textDecoration: 'underline', backgroundColor: 'transparent'}
                    }}
                  >
                    {accordionOpen ? '' : 'Click here for troubleshooting steps'}
                  </Button>

                  {accordionOpen && (
                    <Box>
                      <Typography variant='body1' gutterBottom>
                        We apologize for any inconvenience this may have caused and appreciate your patience.
                      </Typography>
                      <Typography variant='body1' gutterBottom style={{marginTop: '20px', marginBottom: '10px'}}>
                        Please try the following steps:
                      </Typography>
                      <ul style={{paddingLeft: '1.25rem', marginTop: '2px'}}>
                        <li>
                          <Typography variant='body1'>Clear your browser cache and cookies.</Typography>
                        </li>
                        <li>
                          <Typography variant='body1'>Ensure your browser is up to date.</Typography>
                        </li>
                        <li>
                          <Typography variant='body1'>Try a different device or browser.</Typography>
                        </li>
                      </ul>
                      <Typography variant='body1' sx={{mt: 1}}>
                        If the issue persists, please contact our support team at:{' '}
                        <a href='https://www.afspa.org/secure-form-member-portal-support' target='_blank' rel='noopener noreferrer'>
                          www.afspa.org/secure-form-member-portal-support
                        </a>
                      </Typography>
                      <Typography variant='body1' sx={{mt: 1}}>
                        We are here to assist you and ensure you can access the resources you need. Thank you for your understanding and cooperation.
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Alert>
            </Box>
          )}

          {/* begin::Alert of Notice of Accreditation Survey for the Foreign Service Benefit Plan */}
          {accreditationDisplay && !outageDisplay && !upcomingMaintenanceDisplay ? (
            <div className='mb-5 bg-light-primary p-8 rounded self-stretch'>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column'
                }}
              >
                <div className='text-primary' style={{fontSize: '14px'}}>
                  Notice of Accreditation Survey for the Foreign Service Benefit Plan
                </div>

                <a
                  href='https://www.afspa.org/newsitems/aaahc-accreditation-association-for-ambulatory-health-care-notice-of-public-survey-2/'
                  target='_blank'
                  rel='noreferrer'
                  style={{alignSelf: 'flex-end', marginTop: '10px'}}
                >
                  <Button variant='outlined' color='primary' style={{background: '#daeaf5', fontSize: '12px'}}>
                    More Information
                  </Button>
                </a>
              </div>
            </div>
          ) : null}
          {/* begin::Notice of Accreditation Survey for the Foreign Service Benefit Plan */}

          {/* begin::Alert of Upcoming Maintenance */}
          {upcomingMaintenanceDisplay && !outageDisplay ? (
            <Box sx={{width: '100%', my: 1}}>
              <Alert
                severity='warning'
                sx={{
                  py: 3,
                  px: 3,
                  borderRadius: 2,
                  boxShadow: 2,
                  backgroundColor: '#fff8e1',
                  border: '1px solid #ffe082'
                }}
                action={
                  <IconButton aria-label='close' color='inherit' size='small' onClick={() => setUpcomingMaintenanceDisplay(false)}>
                    <CloseIcon fontSize='inherit' />
                  </IconButton>
                }
              >
                <Box sx={{width: '100%', mx: 'auto'}}>
                  <Box
                    component='p'
                    sx={{
                      fontSize: '1rem',
                      lineHeight: 1.0,
                      m: 1
                    }}
                  >
                    <AlertTitle sx={{fontSize: '1.2rem', fontWeight: 'bold'}}>Maintenance Notice:</AlertTitle>
                    <p>The system will be undergoing maintenance on Tuesday, 7/28/2026 from 9:30pm to 10pm EST.</p>
                    <p>We apologize for any inconvenience.</p>
                  </Box>
                </Box>
              </Alert>
            </Box>
          ) : null}

          {/* begin::Alert of Upcoming Maintenance */}

          {formik.status ? (
            <div className='mb-lg-15 alert alert-danger'>
              <div className='alert-text font-weight-bold'>{formik.status}</div>
            </div>
          ) : null}

          {/* begin::Form group */}
          <div className='fv-row mb-8'>
            <label className='form-label fs-6 fw-bolder text-dark'>Username</label>
            <input
              placeholder='Username'
              {...formik.getFieldProps('username')}
              className={clsx(
                'form-control bg-transparent',
                {'is-invalid': formik.touched.username && formik.errors.username},
                {
                  'is-valid': formik.touched.username && !formik.errors.username
                }
              )}
              type='text'
              name='username'
              autoComplete='off'
            />
            {formik.touched.username && formik.errors.username && (
              <div className='fv-plugins-message-container'>
                <span role='alert'>{formik.errors.username}</span>
              </div>
            )}
          </div>
          {/* end::Form group */}

          {/* begin::Form group */}
          <div className='fv-row mb-3'>
            <label className='form-label fw-bolder text-dark fs-6 mb-0'>Password</label>
            <input
              type='password'
              autoComplete='off'
              {...formik.getFieldProps('password')}
              className={clsx(
                'form-control bg-transparent',
                {
                  'is-invalid': formik.touched.password && formik.errors.password
                },
                {
                  'is-valid': formik.touched.password && !formik.errors.password
                }
              )}
            />
            {formik.touched.password && formik.errors.password && (
              <div className='fv-plugins-message-container'>
                <div className='fv-help-block'>
                  <span role='alert'>{formik.errors.password}</span>
                </div>
              </div>
            )}
          </div>
          {/* end::Form group */}

          {/* begin::Wrapper */}
          <div className='d-flex flex-stack flex-wrap gap-3 fs-base fw-semibold mb-8'>
            <div />

            {/* begin::Link */}
            <Link to='/auth/forgot-password/forgot' className='link-primary'>
              Forgot Password ?
            </Link>
            {/* end::Link */}
          </div>
          {/* end::Wrapper */}

          {/* begin::Action */}
          <div className='d-grid mb-10'>
            <button type='submit' id='kt_sign_in_submit' className='btn btn-primary' disabled={formik.isSubmitting || !formik.isValid}>
              {!loading && <span className='indicator-label'>Sign In</span>}
              {loading && (
                <span className='indicator-progress' style={{display: 'block'}}>
                  Please wait...
                  <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                </span>
              )}
            </button>
          </div>
          {/* end::Action */}

          <div className='d-grid mb-10'>
            <Button type='submit' className='btn btn-flex btn-outline btn-text-primary btn-active-color-primary flex-center text-nowrap w-100' onClick={() => navigate('/auth/loginEmail')}>
              Sign In Without A Password
            </Button>
          </div>
          {/* end::Action */}

          <div className='text-gray-500 text-center fw-semibold fs-6'>
            Not a Member yet?{' '}
            <Link to='/auth/forgot-password/register' className='link-primary'>
              Register
            </Link>
          </div>
        </form>
      )}
    </>
  )
}
